import {useOptimisticData} from '@github-ui/optimistic-connection/useOptimisticData'
import {useQuery} from '@github-ui/react-query'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import {Link} from '@primer/react'

import {LABELS} from '../../constants/labels'
import {useClearProjectItemFieldValueMutation} from '../../mutations/clear-project-item-field-value'
import {applyProjectItemFieldValue} from '../../mutations/project-item-field-value-optimistic'
import {useUnarchiveProjectItemMutation} from '../../mutations/unarchive-project-item'
import {useUpdateProjectItemFieldValueMutation} from '../../mutations/update-project-item-field-value'
import {projectItemSectionFieldListQueryOptions} from './fetch-project-item-section-field-list'
import fieldListStyles from './ProjectItemSectionFieldList.module.css'
import {ProjectItemSectionFields} from './ProjectItemSectionFields'
import {ProjectItemSectionFieldsLoading} from './ProjectItemSectionFieldsLoading'
import styles from './ProjectsSections.module.css'

export type ProjectItemSectionFieldListProps = {
  projectItemId: string
  owner: string
  repo: string
  issueNumber: number
  onIssueUpdate: () => void
}

export function ProjectItemSectionFieldList({
  projectItemId,
  owner,
  repo,
  issueNumber,
  onIssueUpdate,
}: ProjectItemSectionFieldListProps) {
  const {addToast} = useToastContext()
  const queryOptions = projectItemSectionFieldListQueryOptions(owner, repo, issueNumber, projectItemId)
  const query = useQuery(queryOptions)
  const projectItem = useOptimisticData(query.data, {
    applyEntityFieldItem: applyProjectItemFieldValue,
    targets: [{queryKey: queryOptions.queryKey}],
  })
  const clearProjectItemFieldValue = useClearProjectItemFieldValueMutation({
    owner,
    repo,
    issueNumber,
    projectItemId,
    queryKey: queryOptions.queryKey,
    onCompleted: onIssueUpdate,
  })
  const updateProjectItemFieldValue = useUpdateProjectItemFieldValueMutation({
    owner,
    repo,
    issueNumber,
    projectItemId,
    queryKey: queryOptions.queryKey,
    onCompleted: onIssueUpdate,
  })

  const restore = useUnarchiveProjectItemMutation({
    owner,
    repo,
    issueNumber,
    projectItemId,
    onCompleted: () => {
      // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
      addToast({type: 'success', message: LABELS.archivedSuccess})
    },
    onError: () => {
      // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
      addToast({type: 'error', message: LABELS.archivedError})
    },
  })

  if (query.isPending) return <ProjectItemSectionFieldsLoading />
  if (query.isError) throw query.error
  if (!projectItem) return <ProjectItemSectionFieldsLoading />

  const canRestore = projectItem.isArchived && projectItem.project.viewerCanUpdate

  return (
    <>
      {canRestore && (
        <ul className={styles.FieldListUl}>
          <li className={styles.ArchivedLi}>
            <span className={styles.FieldListLiText}>
              {LABELS.archivedDescription}
              &nbsp;
              <Link onClick={restore} className={fieldListStyles.restoreLink}>
                Restore
              </Link>
            </span>
          </li>
        </ul>
      )}
      <ProjectItemSectionFields
        onIssueUpdate={onIssueUpdate}
        onClearFieldValue={clearProjectItemFieldValue}
        onUpdateFieldValue={updateProjectItemFieldValue}
        projectItem={projectItem}
      />
    </>
  )
}
